import { Element, useEditor, useNode } from "@craftjs/core";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";

import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "../../block-wrapper";
import { EventCard } from "./event-card/event-card-component";
import { EventCardSkeleton } from "./event-card/event-card-skeleton";
import { NoEventCard } from "./event-card/no-event-card-component";
import {
  DotButton,
  Next,
  Prev,
  useEmblaPagination,
} from "./pagination-dots/pagination-dots-component";
import RelatedEventsSettings from "./related-events-settings-component";
import {
  CenteredHeader,
  Dotcontainer,
  Embla,
  EmblaContainer,
  EmblaSlide,
  EmblaViewport,
  EventsSlide,
  SidePanelLeft,
  SidePanelRight,
} from "./styles";
import useSeriesEvents from "./use-series-events";

export default function RelatedEventsSection() {
  const { t } = useTranslation();
  const {
    props: { seriesExternalId },
    connectors: { connect },
    id: nodeId,
  } = useNode((node) => {
    return {
      nodeId: node.id,
      props: node.data.props,
    };
  });
  const { isEditing } = useEditor((state) => ({
    isEditing: state.options.enabled,
  }));

  const flags = useFlags();
  const {
    viewportRef,
    allSeriesIds,
    seriesEvents,
    seriesEventsLoadingStatus,
    isSeriesAvailable,
  } = useSeriesEvents(seriesExternalId || "");

  // Get current series events to check slide count
  const currentSeriesEvents = seriesEvents?.[seriesExternalId] || [];
  const hasMultipleSlides = currentSeriesEvents.length > 2;

  // Only add WheelGesturesPlugin if there are multiple slides
  const [emblaRef, emblaApi] = useEmblaCarousel(
    undefined,
    hasMultipleSlides ? [WheelGesturesPlugin()] : []
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  } = useEmblaPagination(emblaApi);

  const hasValidSeriesData = (): boolean => {
    // Feature flag check
    if (!flags.eventSeries) {
      return false;
    }

    // Series ID validation and existence check
    if (!isSeriesAvailable) {
      return false;
    }

    // Events data validation - Don't show the block in preview mode if there are no events
    if (
      currentSeriesEvents.length === 0 &&
      !isEditing &&
      seriesEventsLoadingStatus &&
      seriesEventsLoadingStatus !== "loading"
    ) {
      return false;
    }

    if (seriesEventsLoadingStatus === "error" && !isEditing) {
      return false; // Don't show the block in preview mode if there was an error fetching events
    }
    // This means the series ID is valid, the series exists, and there are events available or its editing mode with no events
    return true;
  };

  if (!hasValidSeriesData()) {
    return null;
  }

  const renderEventSlides = () => {
    const slides = [];

    if (seriesEventsLoadingStatus === "loading") {
      // Show skeletons while loading
      return (
        <EmblaSlide>
          <EventsSlide>
            <EventCardSkeleton />
            <EventCardSkeleton />
          </EventsSlide>
        </EmblaSlide>
      );
    }

    if (seriesEventsLoadingStatus === "error") {
      return (
        <NoEventCard message={t("event-series.block.error-fetching-events")} />
      );
    }

    // Events data validation
    if (!currentSeriesEvents.length) {
      return (
        <NoEventCard message={t("event-series.block.no-events-available")} />
      );
    }
    for (let i = 0; i < currentSeriesEvents.length; i += 2) {
      const slide = currentSeriesEvents.slice(i, i + 2);
      slides.push(
        <EmblaSlide key={slide[0].event_url + slide[1]?.event_url}>
          <EventsSlide>
            {slide.map((item) => (
              <EventCard
                key={item.event_url}
                title={item.name}
                description={item.description}
                startTime={item.time_start}
                endTime={item.time_end}
                logoURL={item.medium_picture}
                eventUrl={item.event_url}
                seriesExternalId={seriesExternalId}
              />
            ))}
          </EventsSlide>
        </EmblaSlide>
      );
    }
    return slides;
  };

  if (!flags.eventSeries || allSeriesIds.size === 0) {
    return null;
  }

  // In non-editing mode, if there are no events and the loading status is empty, return an empty block to trigger inviewport logic
  // to load the events when the block is in viewport
  if (
    currentSeriesEvents.length === 0 &&
    !isEditing &&
    !seriesEventsLoadingStatus
  ) {
    return <BlockPaddingWrapper className="opacity-0" ref={viewportRef} />;
  }

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockPaddingWrapper ref={viewportRef}>
        <BlockMaxWidthWrapper>
          <CenteredHeader>
            <Element
              id="event-series-headline"
              is={Text}
              element="h2"
              text={t("event-series.block.default-title")}
              pattern="headingTwo"
              data-testid="event-series-headline"
            />
          </CenteredHeader>

          <Embla>
            <SidePanelLeft className={!hasMultipleSlides ? "invisible" : ""}>
              <Prev onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
            </SidePanelLeft>

            <EmblaViewport ref={emblaRef}>
              <EmblaContainer>{renderEventSlides()}</EmblaContainer>
            </EmblaViewport>

            <SidePanelRight className={!hasMultipleSlides ? "invisible" : ""}>
              <Next onClick={onNextButtonClick} disabled={nextBtnDisabled} />
            </SidePanelRight>

            <Dotcontainer hasMultiPages={scrollSnaps?.length > 1}>
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  $isSelected={index === selectedIndex}
                />
              ))}
            </Dotcontainer>
          </Embla>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

RelatedEventsSection.craft = {
  custom: {
    displayName: "Related events",
  },
  related: {
    settings: RelatedEventsSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
